import Model, { attr, belongsTo } from '@ember-data/model'
import { service } from '@ember/service'
import { Type } from '@warp-drive/core-types/symbols'
import type { IntlService } from 'ember-intl'
import type School from 'district-ui-client/models/school'
import type { SubscriptionType } from 'district-ui-client/domain/subscription-type'
import type DateTransform from 'district-ui-client/transforms/date'
import type StringTransform from 'district-ui-client/transforms/string'

// the key the BE provides as a capacity string when it is unlimited
export const UNLIMITED_CAPACITY_KEY = 'unlimited'

export type SubscriptionStatus = 'pending' | 'expired' | 'active'

/**
 * A school may have any number of school subscriptions, expired, pending or active.
 */
export default class SchoolSubscription extends Model {
  @service intl!: IntlService

  // date transform expects ISO8601 format, backend provides as yyyy-MM-dd format which is technically still ISO8601
  @attr<DateTransform>('date') declare endDate: Date

  @attr<DateTransform>('date') declare startDate: Date

  @attr<StringTransform>('string') declare status: SubscriptionStatus

  // a number (as string) or 'unlimited'
  @attr<StringTransform>('string') declare private softCapacity: string

  @attr<StringTransform>('string') declare subscriptionType: SubscriptionType

  @belongsTo('school', { async: false, inverse: 'schoolSubscriptions' }) declare school?: School

  get isPending() {
    return this.status === 'pending'
  }

  get isExpired() {
    return this.status === 'expired'
  }

  get isActive() {
    return this.status === 'active'
  }

  get formattedCapacity(): string {
    return this.softCapacity === UNLIMITED_CAPACITY_KEY ? this.intl.t('unlimited') : this.softCapacity
  }

  [Type] = 'school-subscription' as const
}
